import { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { useMap } from "@vis.gl/react-google-maps";
import {
  ArrowLeft,
  List,
  MessageSquare,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { MapMarker } from "@/components/map/MapMarker";
import { MapTooltip } from "@/components/map/MapTooltip";
import { LocationSidebar } from "@/components/map/LocationSidebar";
import { OwnershipBadge } from "@/components/locations/OwnershipBadge";
import { ClassificationBadge } from "@/components/locations/ClassificationBadge";
import { EditCollectionDialog } from "@/components/collections/EditCollectionDialog";
import { SyncStatusBadge } from "@/components/collections/SyncStatusBadge";
import { PushToProductionButton } from "@/components/collections/PushToProductionButton";
import { ExportButton } from "@/components/export/ExportButton";
import { ViewShareDialog } from "@/components/collections/ViewShareDialog";
import { MappingShareDialog } from "@/components/physical-mapping/MappingShareDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Share2, MapPinned } from "lucide-react";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { useClusteredMarkers } from "@/hooks/useClusteredMarkers";
import { useMapTooltip } from "@/hooks/useMapTooltip";
import { cn } from "@/lib/utils";
import type { OwnershipType, CollectionSyncStatus } from "@/types/domain";
import { resolveHasSportField } from "@/lib/classification";
import { toast } from "sonner";

function getConfidence(
  classifications: Array<{ confidence: number | null; source: string }> | null,
): number | null {
  if (!classifications || classifications.length === 0) return null;
  const satellite = classifications.find((c) => c.source === "satellite");
  return satellite?.confidence ?? classifications[0]?.confidence ?? null;
}

interface CollectionMapContentProps {
  mapData: any[];
  onHoveredLocationChange: (id: string | null) => void;
  onSelectedLocationChange: (id: string | null) => void;
  selectedLocationId: string | null;
}

function CollectionMapContent({
  mapData,
  onHoveredLocationChange,
  onSelectedLocationChange,
  selectedLocationId,
}: CollectionMapContentProps) {
  const map = useMap();
  const { setMarkerRef, getMarker } = useClusteredMarkers(false);
  const { tooltipLocationId, anchorElement, onMarkerMouseEnter, onMarkerMouseLeave, closeTooltip } =
    useMapTooltip({ getMarker });

  // Sync hovered location to parent for row highlighting
  useEffect(() => {
    onHoveredLocationChange(tooltipLocationId);
  }, [tooltipLocationId, onHoveredLocationChange]);

  // Close tooltip on map click or drag
  useEffect(() => {
    if (!map) return;
    const clickListener = map.addListener("click", () => closeTooltip());
    const dragListener = map.addListener("dragstart", closeTooltip);
    return () => {
      clickListener.remove();
      dragListener.remove();
    };
  }, [map, closeTooltip]);

  // Pre-compute marker data for O(1) tooltip lookup
  const markerDataMap = useMemo(() => {
    const m = new Map<string, {
      id: string;
      name: string;
      sportTypes: string[] | null;
      ownership: string;
      confidence: number | null;
      address: string | null;
    }>();
    for (const loc of mapData) {
      m.set(loc.id, {
        id: loc.id,
        name: loc.name,
        sportTypes: null,
        ownership: loc.ownership,
        confidence: getConfidence(loc.classifications),
        address: loc.address ?? null,
      });
    }
    return m;
  }, [mapData]);

  const handleMarkerClick = useCallback(
    (id: string) => {
      onSelectedLocationChange(id);
    },
    [onSelectedLocationChange],
  );

  const handleViewDetails = useCallback(
    (id: string) => {
      closeTooltip();
      onSelectedLocationChange(id);
    },
    [closeTooltip, onSelectedLocationChange],
  );

  const handleZoomToLocation = useCallback(
    (locationId: string) => {
      const loc = mapData.find((l: any) => l.id === locationId);
      if (map && loc) {
        map.panTo({ lat: loc.lat, lng: loc.lng });
        map.setZoom(18);
        map.setMapTypeId("hybrid");
        const sidebarEl = document.querySelector<HTMLElement>("[data-location-sidebar]");
        const offset = sidebarEl
          ? sidebarEl.offsetWidth / 2
          : window.innerWidth >= 1536 ? 280
          : window.innerWidth >= 1280 ? 240
          : 220;
        map.panBy(offset, 0);
      }
    },
    [map, mapData],
  );

  return (
    <>
      {mapData.map((loc: any) => {
        const hasSportField = resolveHasSportField(loc.classifications);
        return (
          <MapMarker
            key={loc.id}
            locationId={loc.id}
            position={{ lat: loc.lat, lng: loc.lng }}
            hasSportField={hasSportField}
            primaryType={loc.primary_type}
            name={loc.name}
            ownership={loc.ownership}
            isReviewed={loc.is_reviewed ?? false}
            isRejected={loc.is_rejected ?? false}
            isSelected={loc.id === selectedLocationId}
            onClick={handleMarkerClick}
            onMouseEnter={onMarkerMouseEnter}
            onMouseLeave={onMarkerMouseLeave}
            setMarkerRef={setMarkerRef}
          />
        );
      })}

      {/* Hover tooltip */}
      {tooltipLocationId && anchorElement && markerDataMap.has(tooltipLocationId) && (
        <MapTooltip
          locationId={tooltipLocationId}
          anchor={anchorElement}
          markerData={markerDataMap.get(tooltipLocationId)!}
          onClose={closeTooltip}
          onAddToCollection={() => {}}
          onViewDetails={handleViewDetails}
        />
      )}

      {/* Location sidebar */}
      <LocationSidebar
        locationId={selectedLocationId}
        onClose={() => onSelectedLocationChange(null)}
        onEnterSpotPlacement={() => {}}
        onExitSpotPlacement={() => {}}
        isSpotPlacementActive={false}
        onZoomToLocation={handleZoomToLocation}
      />
    </>
  );
}

export function CollectionDetailPage() {
  const params = useParams({ strict: false });
  const collectionId = (params as { collectionId?: string })
    .collectionId as string;
  const navigate = useNavigate();

  const [showEdit, setShowEdit] = useState(false);
  const [showMappingShare, setShowMappingShare] = useState(false);
  const [showViewShare, setShowViewShare] = useState(false);
  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  const utils = trpc.useUtils();

  const {
    data: collection,
    isLoading: colLoading,
    error: colError,
  } = trpc.collections.getById.useQuery(
    { id: collectionId },
    { enabled: !!collectionId },
  );

  const { data: mapData } = trpc.collections.getMapData.useQuery(
    { collectionId },
    { enabled: !!collectionId },
  );

  const { data: locationsData, isLoading: locsLoading } =
    trpc.collections.listLocations.useQuery(
      { collectionId, limit: 500 },
      { enabled: !!collectionId },
    );

  const { data: prodConfigured } = trpc.collections.isProdConfigured.useQuery();
  const { data: syncStatus } = trpc.collections.getSyncStatus.useQuery(
    { collectionId },
    { enabled: !!collectionId && !!prodConfigured?.configured },
  );

  const removeMutation = trpc.collections.removeLocations.useMutation({
    onSuccess: () => {
      utils.collections.listLocations.invalidate({ collectionId });
      utils.collections.getMapData.invalidate({ collectionId });
      utils.collections.getById.invalidate({ id: collectionId });
      toast.success("Location removed from collection");
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.collections.delete.useMutation({
    onSuccess: () => {
      utils.collections.list.invalidate();
      toast.success("Collection deleted");
      navigate({ to: "/dashboard/collections" });
    },
    onError: (err) => toast.error(err.message),
  });

  const handleRowClick = useCallback(
    (locationId: string) => {
      setSelectedLocationId(locationId);
    },
    [],
  );

  const handleHoveredLocationChange = useCallback(
    (id: string | null) => setHoveredLocationId(id),
    [],
  );

  const handleSelectedLocationChange = useCallback(
    (id: string | null) => setSelectedLocationId(id),
    [],
  );

  // Compute map center from markers
  const mapCenter =
    mapData && mapData.length > 0
      ? {
          lat:
            mapData.reduce((sum: number, m: any) => sum + m.lat, 0) /
            mapData.length,
          lng:
            mapData.reduce((sum: number, m: any) => sum + m.lng, 0) /
            mapData.length,
        }
      : undefined;

  if (colLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <Skeleton className="h-5 w-5 rounded-full" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="w-[70%] bg-muted animate-pulse" />
          <div className="w-[30%] p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (colError || !collection) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12">
        <p className="text-destructive text-lg font-medium">
          {colError?.message ?? "Collection not found"}
        </p>
        <Button variant="ghost" asChild>
          <Link to="/dashboard/collections">
            <ArrowLeft className="mr-2 size-4" />
            Back to Collections
          </Link>
        </Button>
      </div>
    );
  }

  const locations = locationsData?.locations ?? [];

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-6 py-3 border-b shrink-0">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dashboard/collections">
            <ArrowLeft className="mr-1 size-4" />
            Collections
          </Link>
        </Button>
        <div className="flex items-center gap-2 mr-auto">
          <span
            className="h-3.5 w-3.5 rounded-full"
            style={{ backgroundColor: collection.color }}
          />
          <h1 className="text-lg font-semibold">{collection.name}</h1>
          <span className="text-sm text-muted-foreground">
            ({collection.locationCount})
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowEdit(true)}>
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Edit
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Share2 className="mr-1.5 h-3.5 w-3.5" />
              Share
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => setShowViewShare(true)}>
              <Share2 className="size-4" />
              View share
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowMappingShare(true)}>
              <MapPinned className="size-4" />
              Physical mapping share
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ExportButton collectionId={collectionId} />
        {prodConfigured?.configured && syncStatus && (
          <>
            <SyncStatusBadge
              status={syncStatus.status as CollectionSyncStatus}
              pushed={syncStatus.pushed}
              total={syncStatus.total}
            />
            <PushToProductionButton
              collectionId={collectionId}
              collectionName={collection.name}
            />
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => {
            if (confirm("Delete this collection? Locations will not be affected.")) {
              deleteMutation.mutate({ id: collectionId });
            }
          }}
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          Delete
        </Button>
      </div>

      {/* Split view: map + table */}
      <div className="flex flex-1 overflow-hidden">
        {/* Map */}
        <div className="w-[70%] border-r relative">
          <GoogleMapWrapper
            defaultCenter={mapCenter}
            defaultZoom={mapData && mapData.length > 0 ? 13 : 7}
            className="h-full w-full"
          >
            <CollectionMapContent
              mapData={mapData ?? []}
              onHoveredLocationChange={handleHoveredLocationChange}
              onSelectedLocationChange={handleSelectedLocationChange}
              selectedLocationId={selectedLocationId}
            />
          </GoogleMapWrapper>
        </div>

        {/* Right panel: Table + Chat tabs */}
        <div className="w-[30%] flex flex-col overflow-hidden">
          <Tabs defaultValue="table" className="flex flex-col h-full">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-2 h-auto py-0 shrink-0">
              <TabsTrigger value="table" className="gap-1.5 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2 text-xs">
                <List className="h-3.5 w-3.5" />
                Locations
              </TabsTrigger>
              <TabsTrigger value="chat" className="gap-1.5 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none py-2 text-xs">
                <MessageSquare className="h-3.5 w-3.5" />
                AI Chat
              </TabsTrigger>
            </TabsList>

            <TabsContent value="table" className="flex-1 overflow-hidden mt-0 flex flex-col">
              {collection.description && (
                <p className="px-4 py-2 text-sm text-muted-foreground border-b">
                  {collection.description}
                </p>
              )}
              <div className="flex-1 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Ownership</TableHead>
                      <TableHead>Classification</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locsLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Skeleton className="h-4 w-32" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-16" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-16" />
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      ))
                    ) : locations.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="h-32 text-center text-muted-foreground"
                        >
                          No locations in this collection yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      locations.map((loc: any) => {
                        const classification =
                          loc.classifications?.find(
                            (c: any) => c.source === "satellite",
                          ) ?? loc.classifications?.[0];

                        const isHovered = hoveredLocationId === loc.id;

                        return (
                          <TableRow
                            key={loc.id}
                            className={cn(
                              "cursor-pointer transition-colors",
                              isHovered && "bg-primary/10 ring-1 ring-inset ring-primary/20",
                            )}
                            onClick={() => handleRowClick(loc.id)}
                          >
                            <TableCell>
                              <span className="font-medium max-w-[180px] truncate block">
                                {loc.name}
                              </span>
                              <span className="text-xs text-muted-foreground truncate block max-w-[180px]">
                                {loc.address ?? "-"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <OwnershipBadge
                                ownership={loc.ownership as OwnershipType}
                              />
                            </TableCell>
                            <TableCell>
                              <ClassificationBadge
                                hasSportField={
                                  classification?.has_sport_field ?? null
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeMutation.mutate({
                                    collectionId,
                                    locationIds: [loc.id],
                                  });
                                }}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="chat" className="flex-1 overflow-hidden mt-0">
              <ChatPanel collectionId={collectionId} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <EditCollectionDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        collection={collection}
      />

      <ViewShareDialog
        open={showViewShare}
        onOpenChange={setShowViewShare}
        shareToken={collection.share_token}
      />

      <MappingShareDialog
        open={showMappingShare}
        onOpenChange={setShowMappingShare}
        collectionId={collection.id}
        shareToken={collection.share_token}
        mappingShareEnabled={(collection as any).mapping_share_enabled ?? false}
      />
    </div>
  );
}
